import { css } from '@emotion/react'
import { Fragment, memo, useCallback } from 'react'
import { ChatImage } from '../../../../../types/mfc'
import {
    openAuthenticationDrawer,
    openSignupDrawer,
    openTokensDrawer,
    openWhiteboardDrawer,
} from '../../../../state/modals/state.drawer'
import i18n from '../../../../utilities/i18n'
import Button from '../../../Buttons/Button'
import { palette } from '../../../styles/colors'
import LoungeChatPreview from '../../PreviewPosterImage/LoungeChatPreview'

export const ACTION_REGEX = /\{\{([^]+)\}\}/
const URL_REGEX =
    /(.*)(https?:\/\/(www\.)?[-a-zA-Z0-9@:%._+~#=]{1,256}\.[a-zA-Z0-9()]{1,6}([-a-zA-Z0-9()@:%_+.~#?&//=]*))(.*)/

const getUrl = (urlString: string, queryString = '') => {
    if (
        queryString === '' ||
        queryString.startsWith('?') ||
        queryString.startsWith('/') ||
        queryString.startsWith('#')
    ) {
        return urlString
    } else {
        const indexStart = urlString.indexOf(queryString)
        return urlString.substring(0, indexStart)
    }
}

const getEndOfUrlString = (endOfUrlString: string, queryString = '') => {
    const endString = endOfUrlString + ' '
    if (
        queryString.startsWith('?') ||
        queryString.startsWith('/') ||
        queryString.startsWith('#')
    ) {
        return endString
    } else {
        return queryString + endString
    }
}

const chatButtonStyles = css({
    backgroundColor: palette.topicButtonBackground,
    color: palette.buttonOutlinedText,
    margin: '0.5em',
    fontSize: '0.8125rem',
    '&:hover': {
        backgroundColor: palette.greenMFC,
        color: palette.commonWhite,
    },
})

const openLoginDrawer = () => openAuthenticationDrawer()
const openCreateAccountDrawer = () => openSignupDrawer()
const openBuyTokensDrawer = () => openTokensDrawer()

interface IProcessedMessage {
    darkMode?: boolean
    fullWindow?: boolean
    images?: Array<ChatImage>
    isLounge?: boolean
    note?: string
    onLoad?(event: any): void
    onOpenWhiteboard?(identifier: string | number): void
    onQualifyPurchase?(params: object): void
    shareTip?: boolean
    shareTipMessage?: string
    shareTipConfirmation?: boolean
    slug?: string
    system?: boolean
    text: string
    tip?: boolean
    title?: string
    url?: string
    html?: boolean
}

const ProcessedMessage = memo((props: IProcessedMessage) => {
    const {
        url,
        tip,
        html,
        slug,
        note,
        text,
        title,
        images,
        system = false,
        isLounge = false,
        shareTip,
        shareTipMessage,
        fullWindow,
        darkMode = false,
        shareTipConfirmation,
        onLoad,
        onOpenWhiteboard,
        onQualifyPurchase,
    } = props

    const openWhiteboard = useCallback(() => {
        if (!fullWindow) {
            onOpenWhiteboard(slug)
        } else {
            openWhiteboardDrawer(slug)
        }
    }, [onOpenWhiteboard, fullWindow, slug])

    // #868: edge case where FCS sends a `null` text value
    if (!text) return null

    if (shareTip) {
        return (
            <Fragment>
                {`${text} for `}
                <a href={url} target="_blank" rel="noopener noreferrer">
                    {title}
                </a>
                {shareTipMessage && ` - ${shareTipMessage}`}
            </Fragment>
        )
    }

    if (shareTipConfirmation) {
        return <span dangerouslySetInnerHTML={{ __html: text }} />
    }

    if (html) {
        return <div dangerouslySetInnerHTML={{ __html: text }} />
    }

    // When Safari supports RegEx lookahead/lookbehind, we can use: text.split(/ |(?=:)[0-9]*(?<=:)/).map
    // Until then, we can use: text.split(/ |(:[0-9]*:)/).filter((e) => e !== '' && e !== undefined).map
    const processedText = (tip && note ? `${text}: ${note}` : text)
        .split(/ |(:[0-9]*:)/)
        .filter((e) => e !== '' && e !== undefined)
        .map((word, key) => {
            if (isLounge) {
                // keep this separate so a user can never accidentally/intentionally match a system message action
                if (ACTION_REGEX.test(word)) {
                    const [, action] = ACTION_REGEX.exec(word)
                    if (action.indexOf('model') !== -1) {
                        const actionTokens = action.split('|')
                        const { [1]: modelId = -1 } = actionTokens
                        if (!(Number(modelId) > 0)) {
                            return (
                                <Fragment key={word + key}>
                                    {word + ' '}
                                </Fragment>
                            )
                        }
                        return (
                            <LoungeChatPreview
                                key={action + key}
                                modelId={Number(modelId)}
                                loungeSlug={slug}
                                onLoad={onLoad}
                                fullWindow={fullWindow}
                                darkMode={darkMode}
                            />
                        )
                    }
                }
            }
            if (system) {
                if (ACTION_REGEX.test(word)) {
                    const [, action] = ACTION_REGEX.exec(word)
                    if (action === 'login') {
                        return (
                            <a
                                key={action + key}
                                onClick={openLoginDrawer}
                                data-testid="Chat__InlineLogin"
                            >
                                {i18n.t('chat.login')}
                            </a>
                        )
                    }
                    if (action === 'register') {
                        return (
                            <a
                                key={action + key}
                                onClick={openCreateAccountDrawer}
                                data-testid="Chat__InlineRegister"
                            >
                                {i18n.t('chat.createFreeAccount')}
                            </a>
                        )
                    }
                    if (action === 'buyTokens') {
                        return (
                            <a
                                key={action + key}
                                onClick={openBuyTokensDrawer}
                                data-testid="Chat__InlineBuyTokens"
                            >
                                {i18n.t('chat.buyTokens')}
                            </a>
                        )
                    }
                    if (action === 'whiteboard') {
                        return (
                            <Button
                                size="medium"
                                css={chatButtonStyles}
                                key={action + key}
                                onClick={openWhiteboard}
                                data-testid="Chat__InlineWhiteboard"
                            >
                                {i18n.t('chat.whiteboard')}
                            </Button>
                        )
                    }
                    if (action.indexOf('MFCShareAlbums') !== -1) {
                        const actionTokens = action.split('|')
                        const { [1]: shareUrl, [2]: albums } = actionTokens
                        const albumsText = `${albums} Album${
                            Number(albums) > 1 ? 's' : ''
                        }`
                        return (
                            <Button
                                size="medium"
                                css={chatButtonStyles}
                                key={action + key}
                                data-testid="Chat__InlineMFCShareAlbums"
                                onClick={() => {
                                    window.open(shareUrl, '_blank')
                                }}
                            >
                                {albumsText}
                            </Button>
                        )
                    }
                    if (action.indexOf('MFCShareRecordings') !== -1) {
                        const actionTokens = action.split('|')
                        const { [1]: shareUrl, [2]: recordings } = actionTokens
                        const recordingsText = `${recordings} Recording${
                            Number(recordings) > 1 ? 's' : ''
                        }`
                        return (
                            <Button
                                size="medium"
                                css={chatButtonStyles}
                                key={action + key}
                                data-testid="Chat__InlineMFCShareRecordings"
                                onClick={() => {
                                    window.open(shareUrl, '_blank')
                                }}
                            >
                                {recordingsText}
                            </Button>
                        )
                    }
                    if (action.indexOf('QuickBuy') !== -1) {
                        const actionTokens = action.split('|')
                        const { [1]: volume } = actionTokens
                        const inlineQuickBuy = () =>
                            onQualifyPurchase({
                                in_chat_purchase: 1,
                                volume,
                                volume_type: 'token',
                            })
                        return (
                            <a
                                key={action + key}
                                onClick={inlineQuickBuy}
                                data-testid="Chat__InlineQuickBuy"
                            >
                                {volume}
                            </a>
                        )
                    }
                }
            }

            if (!/:\d+:/.test(word)) {
                if (!URL_REGEX.test(word)) {
                    return <Fragment key={word + key}>{word + ' '}</Fragment>
                }

                const {
                    1: before,
                    2: urlString,
                    4: queryString,
                    5: afterString,
                } = word.match(URL_REGEX)
                const url = getUrl(urlString, queryString)
                const after = getEndOfUrlString(afterString, queryString)
                return (
                    <Fragment key={word + key}>
                        {before}
                        <a target="_blank" rel="noopener noreferrer" href={url}>
                            {url}
                        </a>
                        {after}
                    </Fragment>
                )
            }

            if (!images || images.length === 0) {
                return <Fragment key={word + key}>{word + ' '}</Fragment>
            }
            const [, count] = word.match(/:(\d+):/)
            const index = Number(count) - 1
            if (index > images.length - 1) {
                return <Fragment key={word + key}>{word + ' '}</Fragment>
            }
            const { shortcut, size, src } = images[index]
            const { height = 0, width = 0 } = size
            const base = {
                style: {
                    height:
                        onLoad && Number(height) > 0
                            ? Number(height)
                            : undefined,
                    width:
                        onLoad && Number(width) > 0 ? Number(width) : undefined,
                    maxWidth: '100%',
                    marginRight: 4,
                    marginLeft: 4,
                },
                title: ':' + shortcut,
                // key: shortcut + key,
                alt: shortcut,
                src,
            }

            return (
                <img
                    key={shortcut + key}
                    {...base}
                    {...(onLoad ? { onLoad } : {})}
                />
            )
        })

    return <Fragment>{processedText}</Fragment>
})

export default ProcessedMessage
